import dayjs from 'dayjs';
import { useEffect, useState } from 'react'
import { firestore } from '../../firebase'
import { SearchOutlined } from '@ant-design/icons'
import { Table, Input, Space, Button, Statistic, Row, Col, Card, Typography, Divider, Skeleton } from 'antd'
import { CSVLink } from 'react-csv'
import Highlighter from 'react-highlight-words'
import moment from 'moment'
import DateRangeSelector from '../dateRangeSelector'
import { affTable, sourceNameLookup } from '../../helpers/sources'

const { Title } = Typography

const LeadsRejected = () => {

  const [isFetching, setIsFetching] = useState(false)
  const [uniqueCount, setUniqueCount] = useState(0)
  const [groupedPartners, setGroupedPartners] = useState([])
  const [groupedZeroBounce, setGroupedZeroBounce] = useState([])
  const [historyTable, setHistoryTable] = useState([])
  const [searchText, setSearchText] = useState(null)
  const [searchedColumn, setSearchedColumn] = useState(null)
  const [fetchStartDate, setFetchStartDate] = useState(null)
  const [fetchEndDate, setFetchEndDate] = useState(null)

  useEffect(() => {

    if (fetchStartDate && fetchEndDate) fetchHistory()

  }, [fetchStartDate, fetchEndDate])

  const fetchHistory = async () => {

    setIsFetching(true)

    try {

      // list of conversions
      const res = await firestore.collection('leads')
        .where('created_at', '>=', new Date(fetchStartDate.startOf('d').valueOf()))
        .where('created_at', '<=', new Date(fetchEndDate.endOf('d').valueOf()))
        .where('rejected', '==', true)
        .get()

      if (res.empty) return

      // --- ALL ---

      const tableData = res.docs.map((x, i) => ({
        ...x.data(),
        key: i,
        email: x.data().email,
        source_name: sourceNameLookup(x.data())
      })).filter(x => !(x.list !== 'BTM' && x.hasOwnProperty('rejected') && !x.hasOwnProperty('queue')))


      // --- GROUPED ---

      // by partner
      const gPartners = tableData
        .reduce((acc, obj) => {

          const category = obj.source_name
          let existingArray = acc.find((arr) => arr[0]?.source_name === category)
        
          if (existingArray) {
            existingArray.push(obj)
          } else {
            acc.push([obj])
          }
        
          return acc
        }, [])

      const gZeroBounce = tableData
        .filter((obj) => affTable.includes(obj.source_name))
        .reduce((acc, obj) => {

          const category = obj.zeroBounce
          let existingArray = acc.find((arr) => arr[0]?.zeroBounce === category)
        
          if (existingArray) {
            existingArray.push(obj)
          } else {
            acc.push([obj])
          }
        
          return acc
        }, [])

      // --- UNIQUE ---
      const unique = new Set()

      tableData.forEach((obj) => {
        unique.add(obj.email)
      })

      setUniqueCount(unique.size)
      setGroupedPartners(gPartners)
      setGroupedZeroBounce(gZeroBounce)
      setHistoryTable(tableData)

    } catch (error) {

      console.error({error})

    } finally {

      // remove loading
      setIsFetching(false)
    }
  }

  const getColumnSearchProps = dataIndex => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={node => { searchInput = node }}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type='primary'
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size='small'
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button onClick={() => handleReset(clearFilters)} size='small' style={{ width: 90 }}>
            Reset
          </Button>
          <Button
            type='link'
            size='small'
            onClick={() => {
              confirm({ closeDropdown: false })
              setSearchText(selectedKeys[0])
              setSearchedColumn(dataIndex)
            }}
          >
            Filter
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : '',
    render: text =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  })

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm()
    setSearchText(selectedKeys[0])
    setSearchedColumn(dataIndex)
  }

  const handleReset = clearFilters => {
    clearFilters()
    setSearchText('')
  }

  const columns = [
    {
      width: 150,
      key: 'created_at',
      title: 'Subscribed on',
      dataIndex: 'created_at',
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.created_at - b.created_at,
      render: text => `${moment(text.toDate()).format('MM/DD/YY hh:mm A')}`
    }, {
      width: 250,
      key: 'source_name',
      title: 'Partner',
      dataIndex: 'source_name',
      sorter: (a, b) => a.source_name.localeCompare(b.source_name),
      ...getColumnSearchProps('source_name')
    }, {
      width: 250,
      key: 'email',
      title: 'Email',
      dataIndex: 'email',
      sorter: (a, b) => a.email.localeCompare(b.email),
      ...getColumnSearchProps('email')
    }, {
      width: 150,
      key: 'offer',
      title: 'Offer',
      dataIndex: 'offer',
      sorter: (a, b) => a.offer.localeCompare(b.offer),
      ...getColumnSearchProps('offer')
    }, {
      width: 150,
      key: 'zeroBounce',
      title: 'Reason',
      dataIndex: 'zeroBounce',
      sorter: (a, b) => a.zeroBounce.localeCompare(b.zeroBounce),
      ...getColumnSearchProps('zeroBounce')
    }, {
      width: 150,
      key: 'zeroBounce_sub',
      title: 'Reason (additional)',
      dataIndex: 'zeroBounce_sub',
      sorter: (a, b) => a.zeroBounce_sub.localeCompare(b.zeroBounce_sub),
      ...getColumnSearchProps('zeroBounce_sub')
    }
  ]

  const determineAllUnqiue = list => {

    const unique = new Set()

    list.forEach((obj) => {
      unique.add(obj.email)
    })

    return unique.size
  }

  return (
    <>
      <Title level={2}>Leads Rejected</Title>

      <Row gutter={[16, 16]} justify='end' align='center'>
        <Col sm={8} md={4} >
          <Button
            type='primary'
            loading={isFetching}
            onClick={fetchHistory}
          >
            Refresh Data
          </Button>
        </Col>
      </Row>

      {(isFetching) &&
        <Skeleton active={true} paragraph={{ rows: 1 }} style={{ marginTop: '-1.25rem' }} />
      }

      {!(isFetching) &&
        <Row gutter={[16, 16]} justify='space-between' align='middle'>
          <Col xm={24} sm={16} style={{ display: 'flex', flexDirection: 'column' }}>
            <DateRangeSelector fetchStartDate={fetchStartDate} fetchEndDate={fetchEndDate} onChange={({ startDate, endDate }) => {
              setHistoryTable([])
              setGroupedPartners([])
              setFetchStartDate(startDate)
              setFetchEndDate(endDate)
            }} />
          </Col>
        </Row>
      }

      {historyTable.length > 0 &&
        <Row gutter={[16, 16]}>
          <Card variant='borderless'>
            <Statistic title='Total' value={historyTable.length} />
          </Card>
          <Divider type='vertical' />
          <Card variant='borderless'>
            <Statistic title='Unique' value={uniqueCount} />
          </Card>
        </Row>
      }

      {groupedPartners.length > 0 &&
        <>
          <Divider orientation='left'>Partners</Divider>
          <Row gutter={[16, 16]}>
            {groupedPartners.sort((a,b) => b.length - a.length).map((x, i) =>
              <Col key={i} span={4}>
                <Card size='small' title={`${x[0].source_name}`}>
                  <Statistic title='Total' value={x.length} />
                  <Statistic title='Unique' value={determineAllUnqiue(x)} />
                </Card>
              </Col>
            )}
          </Row>
        </>
      }

      {groupedZeroBounce.length > 0 &&
        <>
          <Divider orientation='left'>Rejection Reason</Divider>
          <Row gutter={[16, 16]}>
            {groupedZeroBounce.sort((a,b) => b.length - a.length).map((x, i) =>
              <Col key={i} span={4}>
                <Card size='small' title={`${x[0].zeroBounce}`}>
                  <Statistic title='Total' value={x.length} />
                  <Statistic title='Unique' value={determineAllUnqiue(x)} />
                </Card>
              </Col>
            )}
          </Row>
        </>
      }

      <Row justify='space-between' align='middle'>
        <Table
          style={{ paddingTop: 20 }}
          onChange={(pagination, filters, sorter, extra) => setHistoryTable(extra.currentDataSource)}
          loading={isFetching}
          columns={columns}
          dataSource={historyTable}
          scroll={{ x: 1000, y: 500 }}
          size='small'
          title={() => (
            <CSVLink
              filename={'OptiPub_Leads_Rejected.csv'}
              data={historyTable}
              className='btn btn-primary'
            >
              Export to CSV
            </CSVLink>
          )}
        />
      </Row>
    </>
  )
}

export default LeadsRejected